import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { getSupabaseBrowserClient } from "../integrations/supabase/client";
import type { TenantRoster } from "../lib/actor";
import { sourcedKey, useReadSource, viewRows } from "./readSource";

/**
 * The signed-in tenant's people, so a client surface can name a colleague instead of guessing.
 *
 * Two ordinary RLS-scoped selects, joined here rather than in PostgREST. The relationship is the
 * composite foreign key `memberships (contact_id, tenant_id) -> contacts (id, tenant_id)`, and an
 * embed would have to name that relationship in a select string; a join written in JavaScript over
 * two tenant-filtered reads says the same thing and cannot be broken by a renamed constraint.
 *
 * Nothing new was needed on the server. Migration `20260829120002_identity_tenants_memberships`
 * already grants `authenticated` the columns read below and covers both tables with
 * `memberships_select_member` and `contacts_select_member`, each using
 * `tenant_id = any (current_tenant_ids())`. Both selects still filter on `tenant_id` explicitly,
 * because a person can hold memberships in more than one tenant and the policy would return all of
 * them.
 *
 * **Membership `status` is not filtered.** The question this read answers is "who wrote this", which
 * is a fact about the past, and not "may this person act now", which is what `status` governs. A
 * suspended colleague's old comments keep their author. `20260903210001_phase5b_admin_thread_actor_side`
 * draws the same line on the admin side for `private.admin_users.active`.
 */

/**
 * Both reads are capped, and a full page is treated as a failure rather than as an answer.
 *
 * A truncated roster is not a smaller roster. Every member past the cap silently becomes
 * "Petrovic" on both client surfaces, which is precisely the fault this module exists to remove,
 * and it would appear only for the largest tenant and only in that tenant's own view. The cap is far
 * above any plausible client of this portal, so reaching it means something is wrong, and something
 * wrong is worth an error the reader can see rather than a lie they cannot.
 */
export const TENANT_ROSTER_LIMIT = 500;

export const tenantRosterKey = (tenantId: string) => ["portal", "tenant-roster", tenantId] as const;

/**
 * A failed read throws. It never resolves to a partial or empty roster.
 *
 * The register is `orThrow` in `app/auth/usePortalIdentity.ts`, and the rule is the same one:
 * a failure is rendered as a failure and never as a fact about a person. An empty roster is a
 * perfectly well-formed answer that relabels every one of the client's colleagues as their supplier,
 * so it must not be reachable from a network fault or a revoked grant.
 */
function orThrow<T>(
  result: { data: T; error: { message: string; code?: string } | null },
  what: string
): T {
  if (result.error) {
    throw new Error(
      `PORTAL_ROSTER_READ_FAILED: ${what} could not be read. ${result.error.message}`,
      { cause: result.error }
    );
  }

  return result.data;
}

export async function fetchTenantRoster(tenantId: string): Promise<TenantRoster> {
  const supabase = getSupabaseBrowserClient();

  const [membershipResult, contactResult] = await Promise.all([
    supabase
      .from("memberships")
      .select("user_id, contact_id")
      .eq("tenant_id", tenantId)
      .limit(TENANT_ROSTER_LIMIT),
    supabase
      .from("contacts")
      .select("id, full_name")
      .eq("tenant_id", tenantId)
      .limit(TENANT_ROSTER_LIMIT)
  ]);

  const memberships = orThrow(membershipResult, "the people in your organisation") ?? [];
  const contacts = orThrow(contactResult, "the names in your organisation") ?? [];

  return rosterFrom(memberships, contacts);
}

/**
 * The same roster inside "Visa som kund", read through Petrovic's two gateways for the one client and
 * held to the same cap: the slice stands in for the client's `.limit`, so a full page still throws.
 */
async function fetchTenantRosterInView(tenantId: string): Promise<TenantRoster> {
  const [memberships, contacts] = await Promise.all([
    viewRows<{ user_id: string; contact_id: string }>("admin_as_client_memberships", { p_tenant_id: tenantId }),
    viewRows<{ id: string; full_name: string }>("admin_as_client_contacts", { p_tenant_id: tenantId })
  ]);

  return rosterFrom(
    memberships.slice(0, TENANT_ROSTER_LIMIT).map(({ user_id, contact_id }) => ({ user_id, contact_id })),
    contacts.slice(0, TENANT_ROSTER_LIMIT).map(({ id, full_name }) => ({ id, full_name }))
  );
}

function rosterFrom(
  memberships: readonly { user_id: string; contact_id: string }[],
  contacts: readonly { id: string; full_name: string }[]
): TenantRoster {
  for (const [rows, what] of [
    [memberships, "memberships"],
    [contacts, "contacts"]
  ] as const) {
    if (rows.length >= TENANT_ROSTER_LIMIT) {
      throw new Error(
        `PORTAL_ROSTER_READ_FAILED: ${what} reached the ${TENANT_ROSTER_LIMIT} row cap, so the roster cannot be proven complete.`
      );
    }
  }

  const names = new Map(contacts.map((contact) => [contact.id, contact.full_name]));
  const roster = new Map<string, string>();

  for (const membership of memberships) {
    const name = names.get(membership.contact_id);

    // A skip rather than a fallback, and unreachable as the schema stands: `contact_id` is
    // `not null` behind a composite foreign key into the same tenant's `contacts`, both reads carry
    // the same tenant filter, and the row-cap check above rules out the one way the two pages could
    // disagree. Nothing here invents a name for a membership that has none.
    if (name) {
      roster.set(membership.user_id, name);
    }
  }

  return roster;
}

export function useTenantRoster(tenantId: string | null): UseQueryResult<TenantRoster> {
  const source = useReadSource();
  return useQuery({
    queryKey: sourcedKey(source, tenantRosterKey(tenantId ?? "none")),
    enabled: Boolean(tenantId),
    // `enabled` already gates this on a non-null id; the narrowing is for the type checker.
    queryFn: () =>
      source.kind === "admin" ? fetchTenantRosterInView(source.tenantId) : fetchTenantRoster(tenantId as string)
  });
}
